import {
  HoveredEvents,
  PAEventType
} from '@dadohr/shared/domainTypes/productAnalytics';
import { IReportActor } from '@dadohr/shared/domainTypes/reports';
import styled from '@emotion/styled';
import { Grid, Typography } from '@material-ui/core';
import { sum } from 'lodash';
import { useUnsafeTranslation } from '../../../../i18next';
import { FlexContainer } from '../../../../layout/Flex';
import { useProductAnalytics } from '../../../../services/productAnalytics';
import { useTheme } from '../../../../themes';

type TrackingInfo = {
  name: HoveredEvents;
  explanation: string;
};

const NumberContainer = styled('div')`
  display: flex;
  align-items: flex-end;
  font-size: 2rem;
`;

const GroupContainer = styled('div')`
  display: flex;
  align-items: flex-end;
  font-weight: ${(p) => p.theme.typography.fontWeightMedium};
  text-transform: uppercase;
`;
const ColorContainer = styled('span')`
  width: 12px;
  height: 12px;
  border-radius: 100%;
  background-color: ${(props) => props.color};
  font-weight: ${(p) => p.theme.typography.fontWeightLight};
  margin: 0 4px 4px 0;
`;

const GridTotalCount = styled('div')`
  width: 13%;
`;

export const ReportNumberItem: React.FC<{
  label: string;
  number: number;
  color?: string;
}> = ({ label, number, color }) => {
  const theme = useTheme();

  return (
    <>
      <FlexContainer
        justifyContent="flex-start"
        direction="column"
        alignItems="flex-start"
        spacing={1.5}
      >
        <GroupContainer>
          {color && <ColorContainer color={color} />}
          <Typography
            component="div"
            variant="body2"
            color="textSecondary"
            noWrap={true}
            style={{
              fontSize: '0.8rem',
              fontWeight: theme.typography.fontWeightBold
            }}
          >
            {label}
          </Typography>
        </GroupContainer>
        <NumberContainer>{number}</NumberContainer>
      </FlexContainer>
    </>
  );
};

export const ReportBigNumbers = ({
  data,
  actors,
  totalLabel,
  marginBottom,
  title
}: {
  data: { [actorKey: string]: number };
  actors: IReportActor[];
  totalLabel?: string;
  marginBottom?: number;
  title?: string;
}) => {
  const { t } = useUnsafeTranslation();
  const { trackEvent } = useProductAnalytics();

  const stats: Record<string, TrackingInfo> = {
    people: {
      name: 'People Statistics Hovered',
      explanation:
        'Admin user hovered over the totals for people in different roles involved in experiences'
    },
    tasks: {
      name: 'Tasks Sent Statistics Hovered',
      explanation:
        'Admin user hovered over the totals for tasks sent to different roles as part of an experience'
    },
    messages: {
      name: 'Messages Sent Statistics Hovered',
      explanation:
        'Admin user hovered over the totals for messages sent to different roles as part of an experience'
    }
  };
  return (
    <FlexContainer
      justifyContent="flex-start"
      alignItems="flex-start"
      spacing={2}
      marginBottom={marginBottom}
      onMouseEnter={() =>
        Object.keys(stats).forEach((stat: string) => {
          if (stat === title) {
            trackEvent({
              eventType: PAEventType.OTHER,
              name: stats[stat].name,
              Explanation: stats[stat].explanation
            });
          }
        })
      }
    >
      <GridTotalCount>
        <ReportNumberItem
          label={totalLabel || t('Total')}
          number={sum(Object.values(data))}
        />
      </GridTotalCount>

      <FlexContainer fullWidth wrap="wrap" spacing={3}>
        {actors.map((actor) => (
          <Grid item key={actor.key} xs={6} sm={3} md={2}>
            <ReportNumberItem
              label={actor.label}
              number={data[actor.key] || 0}
              color={actor.color}
            />
          </Grid>
        ))}
      </FlexContainer>
    </FlexContainer>
  );
};
